import './card-configuration.scss';

class CardConfiguration {
    constructor(element) {
        this.$root = element;
        this.$form = this.$root.querySelector('[data-form="root"]');
        this.$cardCount = this.$root.querySelector('[data-card-count]');
        this.$updateCountButton = this.$root.querySelector(
            '[data-card-count-update]',
        );
        this.$nextButton = this.$root.querySelector('[data-em-nav="next"]');
        this.$tabs = [];
        this.$panels = [];
        this.validationUrl = this.$root.dataset.validationUrl;
        this.driveTypeConfig = this.parseJsonData('driveTypeConfig', {});
        this.featureConfig = this.parseJsonData('featureConfig', {});
        this.validationMessages = this.parseJsonData(
            'validationMessages',
            {},
        );
        this.isValidating = false;
        this.lastFeatureChange = null;
        this.panelDriveTypeSelections = {};
    }

    initialize() {
        if (!this.$form) {
            return;
        }

        this.refreshElements();
        this.enableJavaScriptMode();
        this.setEvents();
        this.initializeCounters();
        this.initializeSelectFieldStates();
        this.initializeCardPanels();
        this.initializeCardStates();
    }

    refreshElements() {
        this.$tabs = Array.from(
            this.$root.querySelectorAll('[data-card-tab]'),
        );

        this.$panels = Array.from(
            this.$root.querySelectorAll('[data-card-panel]'),
        );
    }

    enableJavaScriptMode() {
        this.$root.classList.add('is--js');

        const $fallback = this.$root.querySelector(
            '[data-card-count-fallback]',
        );

        if ($fallback) {
            $fallback.hidden = true;
        }

        this.updateContentVisibility();
    }

    updateContentVisibility() {
        const $content = this.$root.querySelector('[data-card-content]');
        const hasCards = this.$tabs.length > 0 && this.$panels.length > 0;

        if ($content) {
            $content.hidden = !hasCards;
        }
    }

    setEvents() {
        this.$cardCount?.addEventListener('change', () => {
            this.updateCardCount();
        });

        this.$root.addEventListener('click', (event) => {
            const $tab = event.target.closest('[data-card-tab]');

            if ($tab) {
                this.handleTabClick($tab);
                return;
            }

            this.handleFeatureCardClick(event);
        });

        this.$root.addEventListener('keydown', (event) => {
            this.handleTabKeyboardNavigation(event);
        });

        this.$root.addEventListener('input', (event) => {
            if (event.target.matches('[data-counter-max]')) {
                this.updateCounter(event.target);
            }
        });

        this.$root.addEventListener('change', (event) => {
            if (event.target.matches('select')) {
                this.updateSelectFieldState(event.target);
            }

            const $panel = event.target.closest('[data-card-panel]');

            if (!$panel) {
                return;
            }

            if (event.target.matches('[data-drive-type-selector]')) {
                this.handleDriveTypeChange($panel);
                return;
            }

            if (event.target.matches('[data-feature]')) {
                this.handleFeatureChange($panel, event.target);
                return;
            }

            if (event.target.matches('[data-tariff] input[type="radio"]')) {
                this.handleTariffChange($panel, event.target);
                return;
            }

            if (event.target.matches('.em-card-configuration__selection-input')) {
                this.syncInputSelectionState(event.target);
                this.rememberPanelDriveTypeSelection($panel);
            }
        });

        if (this.$nextButton) {
            this.$nextButton.addEventListener('click', (event) => {
                this.handleNextClick(event);
            });
        }
    }

    updateCardCount() {
        if (!this.$updateCountButton) {
            return;
        }

        /*
         * The backend owns the Symfony form collection.
         * Trigger the existing Update Count submitter rather than creating
         * new unbound form fields only in JavaScript.
         */
        if (typeof this.$form.requestSubmit === 'function') {
            this.$form.requestSubmit(this.$updateCountButton);
            return;
        }

        this.$updateCountButton.click();
    }

    initializeCardPanels() {
        this.refreshElements();
        this.updateContentVisibility();

        if (!this.$tabs.length || !this.$panels.length) {
            return;
        }

        const $firstInvalidPanel = this.findFirstInvalidPanel();
        const $activeTab =
            ($firstInvalidPanel
                ? this.$tabs.find(
                    ($tab) =>
                        $tab.dataset.cardTab ===
                        $firstInvalidPanel.dataset.cardPanel,
                )
                : this.$tabs.find(
                    ($tab) =>
                        $tab.getAttribute('aria-selected') === 'true',
                )) || this.$tabs[0];

        this.activatePanel($activeTab.dataset.cardTab, false);

        if ($firstInvalidPanel) {
            this.focusFirstError($firstInvalidPanel);
        }
    }

    findFirstInvalidPanel() {
        return (
            this.$panels.find(($panel) =>
                $panel.querySelector(
                    '[data-panel-error] .form__error, ' +
                        '[data-panel-error] [data-js-error], ' +
                        '.form__field--error, ' +
                        '.em-card-configuration__pricing-card--error, ' +
                        '.em-card-configuration__feature-card--error, ' +
                        '.em-card-configuration__selection-card--error',
                ),
            ) || null
        );
    }

    getActiveTab() {
        return this.$tabs.find(
            ($tab) => $tab.getAttribute('aria-selected') === 'true',
        );
    }

    getPanel(cardNumber) {
        return this.$panels.find(
            ($panel) => $panel.dataset.cardPanel === cardNumber,
        );
    }

    async handleTabClick($targetTab) {
        if (this.isValidating) {
            return;
        }

        const $activeTab = this.getActiveTab();

        if (!$activeTab) {
            this.activatePanel($targetTab.dataset.cardTab);
            return;
        }

        if ($activeTab === $targetTab) {
            return;
        }

        const $activePanel = this.getPanel($activeTab.dataset.cardTab);

        if (!$activePanel) {
            return;
        }

        await this.validatePanel(
            $activePanel,
            $activeTab,
            {
                focusOnError: true,
            },
        );

        this.activatePanel($targetTab.dataset.cardTab);
        this.focusPanelErrorsIfPresent(
            this.getPanel($targetTab.dataset.cardTab),
            $targetTab,
        );
    }

    handleTabKeyboardNavigation(event) {
        const $tab = event.target.closest('[data-card-tab]');

        if (
            !$tab ||
            !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)
        ) {
            return;
        }

        event.preventDefault();

        const currentIndex = this.$tabs.indexOf($tab);
        let nextIndex = currentIndex;

        if (event.key === 'ArrowRight') {
            nextIndex = (currentIndex + 1) % this.$tabs.length;
        }

        if (event.key === 'ArrowLeft') {
            nextIndex =
                (currentIndex - 1 + this.$tabs.length) %
                this.$tabs.length;
        }

        if (event.key === 'Home') {
            nextIndex = 0;
        }

        if (event.key === 'End') {
            nextIndex = this.$tabs.length - 1;
        }

        this.$tabs[nextIndex]?.focus();
    }

    activatePanel(cardNumber, focusTab = true) {
        this.$tabs.forEach(($tab) => {
            const isActive = $tab.dataset.cardTab === cardNumber;

            $tab.classList.toggle(
                'em-card-configuration__tab--active',
                isActive,
            );
            $tab.setAttribute('aria-selected', String(isActive));
            $tab.setAttribute('tabindex', isActive ? '0' : '-1');

            if (isActive) {
                $tab.setAttribute('aria-current', 'step');

                if (focusTab) {
                    $tab.focus();
                }
            } else {
                $tab.removeAttribute('aria-current');
            }
        });

        this.$panels.forEach(($panel) => {
            const isActive = $panel.dataset.cardPanel === cardNumber;

            $panel.hidden = !isActive;
            $panel.setAttribute('aria-hidden', String(!isActive));
            this.setPanelFocusableState($panel, isActive);
        });
    }

    setPanelFocusableState($panel, isActive) {
        $panel
            .querySelectorAll(
                'input, select, textarea, button, a[href]',
            )
            .forEach(($element) => {
                if (isActive) {
                    if ($element.dataset.originalTabindex !== undefined) {
                        const originalTabindex =
                            $element.dataset.originalTabindex;

                        if (originalTabindex === '') {
                            $element.removeAttribute('tabindex');
                        } else {
                            $element.setAttribute(
                                'tabindex',
                                originalTabindex,
                            );
                        }

                        delete $element.dataset.originalTabindex;
                    }

                    return;
                }

                if ($element.dataset.originalTabindex === undefined) {
                    $element.dataset.originalTabindex =
                        $element.getAttribute('tabindex') || '';
                }

                $element.setAttribute('tabindex', '-1');
            });
    }

    handleDriveTypeChange($panel) {
        const previousDriveType = this.getStoredPanelDriveType($panel);
        const driveType = this.getPanelDriveType($panel);

        if (previousDriveType) {
            this.rememberPanelDriveTypeSelection($panel, previousDriveType);
        }

        this.setStoredPanelDriveType($panel, driveType);
        this.clearDriveTypeDependentSelections($panel);
        this.updatePanelState($panel);
        this.restorePanelDriveTypeSelection($panel, driveType);
        this.clearStaleTariffErrors($panel);
        this.applyCrossCardRules();
    }

    handleFeatureChange($panel, $feature) {
        const featureName = this.normalizeValue(
            $feature.dataset.feature || '',
        );

        this.lastFeatureChange = {
            driveType: this.getPanelDriveType($panel),
            featureName,
            fieldKey: this.getFieldKey($feature),
            isChecked: Boolean($feature.checked),
        };

        this.syncInputSelectionState($feature);
        this.updateVinVisibility($panel);
        this.rememberPanelDriveTypeSelection($panel);
        this.applyCrossCardRules();
    }

    handleFeatureCardClick(event) {
        const $featureCard = event.target.closest('[data-feature-wrapper]');

        if (!$featureCard) {
            return;
        }

        if (
            event.target.closest(
                'input, label, button, a, [data-tooltip-item]',
            )
        ) {
            return;
        }

        const $featureInput = $featureCard.querySelector('[data-feature]');

        if (!$featureInput || $featureInput.disabled) {
            return;
        }

        $featureInput.click();
    }

    syncInputSelectionState($input) {
        if ($input.type === 'radio' && $input.name) {
            this.syncRadioGroupSelectionState($input);

            if ($input.closest('[data-tariff]') && $input.checked) {
                this.clearStaleTariffErrors(
                    $input.closest('[data-card-panel]'),
                );
            }

            return;
        }

        this.updateSelectionCardState($input);
    }

    handleTariffChange($panel, $input) {
        this.syncInputSelectionState($input);
        this.rememberPanelDriveTypeSelection($panel);
        this.clearStaleTariffErrors($panel);
    }

    syncRadioGroupSelectionState($input) {
        const escapedName =
            typeof CSS !== 'undefined' && CSS.escape
                ? CSS.escape($input.name)
                : $input.name.replace(/([\\"'])/g, '\\$1');

        const selector = `input[type="radio"][name="${escapedName}"]`;
        const $scope = $input.closest('[data-card-panel]') || this.$root;

        $scope.querySelectorAll(selector).forEach(($radio) => {
            this.updateSelectionCardState($radio);
        });
    }

    initializeCardStates() {
        this.$panels.forEach(($panel) => {
            this.updatePanelState($panel);
            this.updateSelectionCardStates($panel);
            this.setStoredPanelDriveType($panel, this.getPanelDriveType($panel));
            this.rememberPanelDriveTypeSelection($panel);
        });

        this.applyCrossCardRules();
    }

    updatePanelState($panel) {
        const driveType = this.getPanelDriveType($panel);
        const hasDriveType = Boolean(driveType);

        const $tariffPanel = $panel.querySelector('[data-tariff-panel]');
        const $featuresPanel = $panel.querySelector(
            '[data-features-panel]',
        );

        if ($tariffPanel) {
            $tariffPanel.hidden = !hasDriveType;
        }

        if ($featuresPanel) {
            $featuresPanel.hidden = !hasDriveType;
        }

        if (!hasDriveType) {
            this.hideDriveTypeDependentContent($panel);
            this.updateTariffContainerClasses($panel);
            return;
        }

        this.updateTariffs($panel, driveType);
        this.updateTariffContainerClasses($panel);
        this.updateFeatures($panel, driveType);
        this.updateFeatureDisclaimers($panel, driveType);
        this.updateVinVisibility($panel);
    }

    hideDriveTypeDependentContent($panel) {
        $panel.querySelectorAll('[data-tariff]').forEach(($wrapper) => {
            const $input = $wrapper.querySelector('input');

            $wrapper.hidden = true;

            if ($input) {
                $input.disabled = true;
                $input.checked = false;
            }
        });

        $panel.querySelectorAll('[data-feature]').forEach(($input) => {
            const $wrapper = $input.closest('[data-feature-wrapper]');

            if ($wrapper) {
                $wrapper.hidden = true;
            }

            $input.disabled = true;
            $input.checked = false;
        });

        this.updateFeatureDisclaimers($panel, null);

        this.updateVinVisibility($panel);
    }

    updateFeatureDisclaimers($panel, driveType) {
        const normalizedDriveType = driveType
            ? this.normalizeValue(driveType)
            : '';

        $panel
            .querySelectorAll('[data-feature-disclaimer-group]')
            .forEach(($item) => {
                const supportedDriveTypes = ($item.dataset.driveTypes || '')
                    .split(',')
                    .map((value) => this.normalizeValue(value))
                    .filter(Boolean);

                if (!normalizedDriveType) {
                    $item.hidden = true;
                    return;
                }

                if (!supportedDriveTypes.length) {
                    $item.hidden = false;
                    return;
                }

                $item.hidden = !supportedDriveTypes.includes(
                    normalizedDriveType,
                );
            });
    }

    clearDriveTypeDependentSelections($panel) {
        $panel.querySelectorAll('[data-tariff] input[type="radio"]').forEach(($input) => {
            $input.checked = false;
        });

        $panel.querySelectorAll('[data-feature]').forEach(($input) => {
            $input.checked = false;
        });
    }

    updateTariffs($panel, driveType) {
        const allowedTariffs = this.getAllowedTariffs(driveType);
        const configuredDefaultTariff = this.getDefaultTariff(driveType);
        const defaultTariff = this.resolveDefaultTariff(
            allowedTariffs,
            configuredDefaultTariff,
        );

        $panel.querySelectorAll('[data-tariff]').forEach(($wrapper) => {
            const tariff = this.normalizeValue(
                $wrapper.dataset.tariff || '',
            );
            const isAllowed = allowedTariffs.includes(tariff);
            const $input = $wrapper.querySelector('input');

            $wrapper.hidden = !isAllowed;
            $wrapper.classList.toggle(
                'em-card-configuration__pricing-card--hidden',
                !isAllowed,
            );

            if (!$input) {
                return;
            }

            $input.disabled = !isAllowed;

            if (!isAllowed) {
                $input.checked = false;
            }

            this.updateSelectionCardState($input);
        });

        this.ensureConfiguredTariffSelection(
            $panel,
            allowedTariffs,
            defaultTariff,
        );
    }

    updateTariffContainerClasses($panel) {
        const $tariffGroup = $panel.querySelector('[data-tariff-group]');

        if (!$tariffGroup) {
            return;
        }

        const $visibleTariffs = Array.from(
            $panel.querySelectorAll('[data-tariff]'),
        ).filter(($wrapper) => !$wrapper.hidden);
        const visibleTariffCount = $visibleTariffs.length;
        const classes = [
            'is--tariff-count-1',
            'is--tariff-count-2',
            'is--tariff-count-3',
        ];

        $tariffGroup.classList.remove(...classes);
        $tariffGroup.dataset.visibleTariffCount = String(visibleTariffCount);
        $tariffGroup.style.removeProperty('--tariff-visible-count');

        if (visibleTariffCount >= 1 && visibleTariffCount <= 3) {
            $tariffGroup.style.setProperty(
                '--tariff-visible-count',
                String(visibleTariffCount),
            );
            $tariffGroup.classList.add(
                `is--tariff-count-${visibleTariffCount}`,
            );
        }
    }

    ensureConfiguredTariffSelection(
        $panel,
        allowedTariffs,
        defaultTariff,
    ) {
        const enabledTariffInputs = this.getEnabledTariffInputs($panel);
        const checkedAllowedInputs = enabledTariffInputs.filter(($input) => {
            const tariff = this.normalizeValue($input.value || '');

            return $input.checked && allowedTariffs.includes(tariff);
        });

        if (checkedAllowedInputs.length > 0) {
            this.syncTariffCardStates($panel);
            return;
        }

        const $defaultWrapper = Array.from(
            $panel.querySelectorAll('[data-tariff]'),
        ).find(
            ($wrapper) =>
                this.normalizeValue(
                    $wrapper.dataset.tariff || '',
                ) === defaultTariff,
        );

        const $defaultInput =
            $defaultWrapper?.querySelector('input:not(:disabled)');

        if ($defaultInput) {
            $defaultInput.checked = true;
        }

        this.syncTariffCardStates($panel);
    }

    updateFeatures($panel, driveType) {
        $panel.querySelectorAll('[data-feature]').forEach(($input) => {
            const $wrapper = $input.closest('[data-feature-wrapper]');
            const allowedDriveTypes = this.getAllowedDriveTypes($input);
            const isAllowed = allowedDriveTypes.includes(driveType);

            if ($wrapper) {
                $wrapper.hidden = !isAllowed;
                $wrapper.classList.toggle(
                    'em-card-configuration__feature-card--hidden',
                    !isAllowed,
                );
            }

            if (!isAllowed) {
                $input.checked = false;
                $input.disabled = true;
            } else {
                $input.disabled = false;
            }

            this.syncInputSelectionState($input);
        });
    }

    parseJsonData(name, fallback) {
        const rawValue = this.$root.dataset[name];

        if (!rawValue) {
            return fallback;
        }

        try {
            return JSON.parse(rawValue);
        } catch (error) {
            return fallback;
        }
    }

    getFeatureConfigKey(featureName) {
        const rawFeatureName = String(featureName || '');

        if (this.featureConfig[rawFeatureName]) {
            return rawFeatureName;
        }

        const normalizedFeatureName = this.normalizeValue(rawFeatureName);

        return (
            Object.keys(this.featureConfig).find(
                (key) =>
                    this.normalizeValue(key) === normalizedFeatureName,
            ) || rawFeatureName
        );
    }

    getConfiguredDriveTypes(featureName) {
        const configKey = this.getFeatureConfigKey(featureName);
        const configuredFeatures = this.featureConfig[configKey] || {};
        const configuredDriveTypes = configuredFeatures.driveTypes || [];

        return configuredDriveTypes.map((value) =>
            this.normalizeValue(value),
        );
    }

    getAllowedDriveTypes($input) {
        const featureName = $input.dataset.feature || '';
        const configuredDriveTypes =
            this.getConfiguredDriveTypes(featureName);

        if (configuredDriveTypes.length) {
            return configuredDriveTypes;
        }

        return ($input.dataset.driveTypes || '')
            .split(',')
            .map((value) => this.normalizeValue(value))
            .filter(Boolean);
    }

    applyCrossCardRules() {
        this.resetCrossCardDisabledStates();
        this.applyExclusiveFeatureRules();
        this.applyInheritedFeatureRules();

        this.$panels.forEach(($panel) => {
            this.updateVinVisibility($panel);
            this.updateSelectionCardStates($panel);
            this.rememberPanelDriveTypeSelection($panel);
        });

        this.lastFeatureChange = null;
    }

    resetCrossCardDisabledStates() {
        this.$panels.forEach(($panel) => {
            const driveType = this.getPanelDriveType($panel);

            $panel.querySelectorAll('[data-feature]').forEach(($input) => {
                const allowedDriveTypes =
                    this.getAllowedDriveTypes($input);
                const isAllowed =
                    Boolean(driveType) &&
                    allowedDriveTypes.includes(driveType);

                $input.disabled = !isAllowed;

                const $wrapper = $input.closest(
                    '[data-feature-wrapper]',
                );

                $wrapper?.classList.remove(
                    'em-card-configuration__feature-card--disabled',
                );
            });
        });
    }

    applyExclusiveFeatureRules() {
        const featureInputs = this.$panels
            .flatMap(($panel) =>
                Array.from($panel.querySelectorAll('[data-feature]')),
            )
            .filter(($input) => !$input.disabled);

        const processedGroups = new Set();

        featureInputs.forEach(($input) => {
            const fieldKey = this.getFieldKey($input);
            const exclusiveWith = String(
                $input.dataset.exclusiveWith || '',
            ).trim();

            if (!fieldKey || !exclusiveWith) {
                return;
            }

            const groupKey = [fieldKey, exclusiveWith]
                .sort()
                .join('::');

            if (processedGroups.has(groupKey)) {
                return;
            }

            processedGroups.add(groupKey);

            const primaryInputs = featureInputs.filter(
                ($candidate) =>
                    this.getFieldKey($candidate) === fieldKey,
            );

            const exclusiveInputs = featureInputs.filter(
                ($candidate) =>
                    this.getFieldKey($candidate) === exclusiveWith,
            );

            this.applyExclusiveFeatureGroupRule(
                fieldKey,
                primaryInputs,
                exclusiveWith,
                exclusiveInputs,
            );
        });
    }
    applyExclusiveFeatureGroupRule(
        primaryFieldKey,
        primaryInputs,
        exclusiveFieldKey,
        exclusiveInputs,
    ) {
        const hasPrimary = primaryInputs.some(($input) => $input.checked);
        const hasExclusive = exclusiveInputs.some(($input) => $input.checked);
        let selectedFieldKey = '';

        if (
            this.lastFeatureChange &&
        this.lastFeatureChange.isChecked &&
        [primaryFieldKey, exclusiveFieldKey].includes(
            this.lastFeatureChange.fieldKey,
        )
        ) {
            selectedFieldKey = this.lastFeatureChange.fieldKey;
        } else if (hasPrimary && !hasExclusive) {
            selectedFieldKey = primaryFieldKey;
        } else if (hasExclusive && !hasPrimary) {
            selectedFieldKey = exclusiveFieldKey;
        } else if (hasPrimary && hasExclusive) {
            selectedFieldKey = this.getFirstCheckedInput(primaryInputs)
                ? primaryFieldKey
                : exclusiveFieldKey;
        }

        if (selectedFieldKey === primaryFieldKey) {
            this.disableExclusiveFeatureGroup(exclusiveInputs, true);
            this.disableExclusiveFeatureGroup(primaryInputs, false);
            return;
        }

        if (selectedFieldKey === exclusiveFieldKey) {
            this.disableExclusiveFeatureGroup(primaryInputs, true);
            this.disableExclusiveFeatureGroup(exclusiveInputs, false);
            return;
        }

        this.disableExclusiveFeatureGroup(primaryInputs, false);
        this.disableExclusiveFeatureGroup(exclusiveInputs, false);
    }

    disableExclusiveFeatureGroup(inputs, disabled) {
        inputs.forEach(($input) => {
            if (disabled && $input.checked) {
                $input.checked = false;
            }

            this.setFeatureDisabled($input, disabled);
        });
    }

    setFeatureDisabled($input, disabled) {
        $input.disabled = disabled;

        const $wrapper = $input.closest('[data-feature-wrapper]');

        $wrapper?.classList.toggle(
            'em-card-configuration__feature-card--disabled',
            disabled,
        );

        $wrapper?.setAttribute('aria-disabled', String(disabled));
        this.updateSelectionCardState($input);
    }

    applyInheritedFeatureRules() {
        const inheritedFeatureNames = this.getInheritedFeatureNames();

        inheritedFeatureNames.forEach((featureName) => {
            const featureInputs = this.getFeatureInputs(
                this.$panels,
                featureName,
            ).filter(($input) => {
                if ($input.disabled) {
                    return false;
                }

                const $panel = $input.closest('[data-card-panel]');
                const driveType = $panel
                    ? this.getPanelDriveType($panel)
                    : '';

                return (
                    Boolean(driveType) &&
                this.getAllowedDriveTypes($input).includes(driveType)
                );
            });

            if (!featureInputs.length) {
                return;
            }

            const isCurrentFeatureChange =
            this.lastFeatureChange &&
            this.normalizeValue(
                this.lastFeatureChange.featureName,
            ) === featureName;

            const targetCheckedState = isCurrentFeatureChange
                ? this.lastFeatureChange.isChecked
                : featureInputs.some(($input) => $input.checked);

            featureInputs.forEach(($input) => {
                if ($input.checked !== targetCheckedState) {
                    $input.checked = targetCheckedState;
                }

                this.syncInputSelectionState($input);
            });
        });
    }

    getFirstCheckedInput(inputs) {
        return inputs.find(($input) => $input.checked) || null;
    }

    updateVinVisibility($panel) {
        const $vinField = $panel.querySelector(
            '[data-visible-when-feature]',
        );
        const dependentFeatureName = $vinField?.dataset.visibleWhenFeature || '';
        const requiresVin = this.isDependentFeatureSelected(
            $panel,
            dependentFeatureName,
        );

        const $vinPanel = $panel.querySelector('[data-vin-panel]');

        if (!$vinPanel) {
            return;
        }

        $vinPanel.hidden = !requiresVin;
        $vinPanel.setAttribute(
            'aria-hidden',
            String(!requiresVin),
        );

        $vinPanel
            .querySelectorAll('input, select, textarea')
            .forEach(($field) => {
                if (requiresVin) {
                    $field.removeAttribute('tabindex');
                    $field.disabled = false;
                    return;
                }

                $field.setAttribute('tabindex', '-1');
                $field.disabled = true;
                if ($field instanceof HTMLInputElement || $field instanceof HTMLTextAreaElement) {
                    $field.value = '';
                }
            });
    }

    isDependentFeatureSelected($panel, featureName) {
        const driveType = this.getPanelDriveType($panel);
        const $featureInput = $panel.querySelector(
            `[data-feature="${featureName}"]`,
        );

        if (
            !$featureInput ||
            !$featureInput.checked ||
            $featureInput.disabled
        ) {
            return false;
        }

        const allowedDriveTypes = this.getAllowedDriveTypes($featureInput);

        return allowedDriveTypes.includes(driveType);
    }

    updateSelectionCardStates($container) {
        $container
            .querySelectorAll(
                '.em-card-configuration__selection-input',
            )
            .forEach(($input) => {
                this.syncInputSelectionState($input);
            });
    }

    updateSelectionCardState($input) {
        const $card = $input.closest(
            '.em-card-configuration__selection-card, ' +
                '.em-card-configuration__pricing-card, ' +
                '.em-card-configuration__feature-card',
        );

        if (!$card) {
            return;
        }

        const isChecked = Boolean($input.checked);
        const isDisabled = Boolean($input.disabled);

        if ($card.classList.contains(
            'em-card-configuration__selection-card',
        )) {
            $card.classList.toggle('is--selected', isChecked);
            $card.classList.toggle('is--disabled', isDisabled);
            $card.setAttribute('aria-disabled', String(isDisabled));
            return;
        }

        $card.classList.toggle('is--selected', isChecked);
        $card.classList.toggle('is--disabled', isDisabled);
        $card.setAttribute('aria-disabled', String(isDisabled));
    }

 

    getPanelDriveType($panel) {
        return this.normalizeValue(
            $panel.querySelector('[data-drive-type-selector]')
                ?.value || '',
        );
    }

 

    getFeatureInputs(panels, featureName) {
        const normalizedFeatureName = this.normalizeValue(featureName);

        return panels.flatMap(($panel) =>
            Array.from($panel.querySelectorAll('[data-feature]')).filter(
                ($input) =>
                    this.normalizeValue($input.dataset.feature || '') ===
                    normalizedFeatureName,
            ),
        );
    }

    getAllowedTariffs(driveType) {
        const tariffs = this.getDriveTypeSettings(driveType).tariffs || [];

        if (tariffs.length) {
            return tariffs.map((tariff) =>
                this.normalizeValue(tariff),
            );
        }

        return [];
    }

    getDefaultTariff(driveType) {
        const defaultTariff = this.getDriveTypeSettings(driveType)
            .defaultTariff;

        return defaultTariff ? this.normalizeValue(defaultTariff) : '';
    }

    getDriveTypeSettings(driveType) {
        return this.driveTypeConfig[this.normalizeValue(driveType)] || {};
    }

    resolveDefaultTariff(allowedTariffs, configuredDefaultTariff) {
        if (allowedTariffs.includes(configuredDefaultTariff)) {
            return configuredDefaultTariff;
        }

        return '';
    }

    normalizeValue(value = '') {
        return String(value)
            .trim()
            .toLowerCase()
            .replace(/[\s-]+/g, '_');
    }

    initializeCounters() {
        this.$root
            .querySelectorAll('[data-counter-max]')
            .forEach(($field) => {
                this.updateCounter($field);
            });
    }

    initializeSelectFieldStates() {
        this.$root
            .querySelectorAll('.form__field--select select')
            .forEach(($select) => {
                this.updateSelectFieldState($select);
            });
    }

    updateSelectFieldState($select) {
        const $field = $select.closest('.form__field--select');

        if (!$field) {
            return;
        }

        const selectedOption = $select.options[$select.selectedIndex] || null;
        const hasValue =
            String($select.value || '').trim() !== '' &&
            Boolean(selectedOption) &&
            !selectedOption.disabled;

        $field.classList.toggle('is--filled', hasValue);
    }

    updateCounter($field) {
        const maximum = Number($field.dataset.counterMax);
        const $counter = this.$root.querySelector(
            `[data-counter-display="${$field.id}"]`,
        );

        if (!$counter || !maximum) {
            return;
        }

        $counter.textContent = `${$field.value.length}/${maximum}`;
    }

    async validatePanel($panel, $tab, { focusOnError = true } = {}) {
        if (!this.validationUrl) {
            return true;
        }

        this.prepareTariffSelectionForValidation($panel);
        this.clearErrors($panel);
        this.setValidationState(true);

        try {
            const response = await fetch(this.validationUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: this.createCardPayload($panel),
            });

            const result = await response.json();

            if (response.ok && result.valid) {
                this.clearTabError($tab);
                return true;
            }

            this.renderErrors($panel, result.errors || {});
            this.setTabError($tab);

            if (focusOnError) {
                this.focusFirstError($panel);
            }

            return false;
        } catch (error) {
            this.clearErrors($panel);
            return false;
        } finally {
            this.setValidationState(false);
        }
    }

    createCardPayload($panel) {
        const payload = new URLSearchParams();

        $panel
            .querySelectorAll('input, select, textarea')
            .forEach(($field) => {
                if (!$field.name || $field.disabled) {
                    return;
                }

                if (
                    ['checkbox', 'radio'].includes($field.type) &&
                    !$field.checked
                ) {
                    return;
                }

                const fieldKey = this.getFieldKey($field);

                if (!fieldKey) {
                    return;
                }

                payload.set(
                    `card_item_form[${fieldKey}]`,
                    $field.value,
                );
            });

        return payload;
    }

    prepareTariffSelectionForValidation($panel) {
        const driveType = this.getPanelDriveType($panel);

        if (driveType) {
            this.updateTariffs($panel, driveType);
        }

        this.syncTariffCardStates($panel);
    }

    syncTariffCardStates($panel) {
        $panel
            .querySelectorAll('[data-tariff] input[type="radio"]')
            .forEach(($input) => {
                this.updateSelectionCardState($input);
            });
    }

    getEnabledTariffInputs($panel) {
        return Array.from(
            $panel.querySelectorAll(
                '[data-tariff] input[type="radio"]:not(:disabled)',
            ),
        );
    }

    getSelectedTariff($panel) {
        const $checkedInput = Array.from(
            $panel.querySelectorAll(
                '[data-tariff] input[type="radio"]:checked',
            ),
        )[0];

        return $checkedInput
            ? this.normalizeValue($checkedInput.value || '')
            : '';
    }

    getStoredPanelDriveType($panel) {
        return this.normalizeValue($panel.dataset.currentDriveType || '');
    }

    setStoredPanelDriveType($panel, driveType) {
        $panel.dataset.currentDriveType = this.normalizeValue(driveType);
    }

    rememberPanelDriveTypeSelection($panel, driveType = this.getStoredPanelDriveType($panel)) {
        const normalizedDriveType = this.normalizeValue(driveType);

        if (!normalizedDriveType) {
            return;
        }

        const panelKey = String($panel.dataset.cardPanel || '');
        const panelSelections = this.panelDriveTypeSelections[panelKey] || {};

        panelSelections[normalizedDriveType] = {
            tariff: this.getSelectedTariff($panel),
            features: this.getSelectedFeatureStates($panel),
        };

        this.panelDriveTypeSelections[panelKey] = panelSelections;
    }

    restorePanelDriveTypeSelection($panel, driveType) {
        const normalizedDriveType = this.normalizeValue(driveType);

        if (!normalizedDriveType) {
            return;
        }

        const panelSelections = this.getPanelDriveTypeSelections($panel);
        const selection = panelSelections[normalizedDriveType];

        if (!selection) {
            return;
        }

        this.restoreTariffSelection($panel, selection.tariff);
        this.restoreFeatureSelections($panel, selection.features);
        this.updateSelectionCardStates($panel);
        this.syncTariffCardStates($panel);
        this.updateVinVisibility($panel);
    }

    getPanelDriveTypeSelections($panel) {
        const panelKey = String($panel.dataset.cardPanel || '');

        return this.panelDriveTypeSelections[panelKey] || {};
    }

    getSelectedFeatureStates($panel) {
        const featureStates = {};

        $panel.querySelectorAll('[data-feature]').forEach(($input) => {
            const fieldKey = this.getFieldKey($input) || this.normalizeValue($input.dataset.feature || '');

            if (!fieldKey) {
                return;
            }

            featureStates[fieldKey] = Boolean($input.checked);
        });

        return featureStates;
    }

    restoreTariffSelection($panel, tariff) {
        const normalizedTariff = this.normalizeValue(tariff);

        if (!normalizedTariff) {
            return;
        }

        $panel.querySelectorAll('[data-tariff] input[type="radio"]').forEach(($input) => {
            $input.checked = this.normalizeValue($input.value || '') === normalizedTariff;
        });
    }

    restoreFeatureSelections($panel, featureStates) {
        $panel.querySelectorAll('[data-feature]').forEach(($input) => {
            const fieldKey = this.getFieldKey($input) || this.normalizeValue($input.dataset.feature || '');

            if (!fieldKey || !(fieldKey in featureStates)) {
                return;
            }

            $input.checked = Boolean(featureStates[fieldKey]);
        });
    }

    getFieldKey($field) {
        if (!$field?.name) {
            return '';
        }

        /*
         * Per-card validation submits a single CardItemForm payload while the
         * page renders a Symfony collection (cards[index][field]). Extract the
         * trailing field key so the request matches the standalone backend form.
         */
        const match = $field.name.match(/\[cards]\[\d+]\[([^\]]+)]$/);

        return match ? match[1] : '';
    }

    setValidationState(isValidating) {
        this.isValidating = isValidating;

        this.$root.classList.toggle(
            'is--validating',
            isValidating,
        );

        this.$tabs.forEach(($tab) => {
            $tab.disabled = isValidating;
        });

        if (this.$nextButton) {
            this.$nextButton.disabled = isValidating;
        }
    }

    setTabError($tab) {
        if (!$tab) {
            return;
        }

        $tab.classList.add(
            'em-card-configuration__tab--error',
        );
        $tab.setAttribute('aria-invalid', 'true');
    }

    clearTabError($tab) {
        if (!$tab) {
            return;
        }

        $tab.classList.remove(
            'em-card-configuration__tab--error',
        );
        $tab.removeAttribute('aria-invalid');
    }

    clearErrors($panel) {
        $panel
            .querySelectorAll('[data-js-error]')
            .forEach(($error) => {
                $error.remove();
            });

        $panel
            .querySelectorAll('.form__field--error')
            .forEach(($field) => {
                $field.classList.remove('form__field--error');
            });

        $panel
            .querySelectorAll(
                '.em-card-configuration__pricing-card--error, ' +
                    '.em-card-configuration__feature-card--error, ' +
                    '.em-card-configuration__selection-card--error',
            )
            .forEach(($card) => {
                $card.classList.remove(
                    'em-card-configuration__pricing-card--error',
                    'em-card-configuration__feature-card--error',
                    'em-card-configuration__selection-card--error',
                );
            });

        this.getErrorContainers($panel).forEach(($container) => {
            $container.innerHTML = '';
        });

        $panel
            .querySelectorAll('[data-tariff] input[type="radio"]')
            .forEach(($input) => {
                $input.removeAttribute('aria-invalid');
            });
    }

    clearStaleTariffErrors($panel) {
        if (!$panel) {
            return;
        }

        const driveType = this.getPanelDriveType($panel);

        if (!driveType) {
            return;
        }

        const selectedTariff = this.getSelectedTariff($panel);
        const allowedTariffs = this.getAllowedTariffs(driveType);

        if (!selectedTariff || !allowedTariffs.includes(selectedTariff)) {
            return;
        }

        const $tariffErrorContainer = $panel.querySelector(
            '[data-tariff-errors]',
        );

        if ($tariffErrorContainer) {
            $tariffErrorContainer.innerHTML = '';
        }

        $panel
            .querySelectorAll(
                '.em-card-configuration__pricing-card--error',
            )
            .forEach(($card) => {
                $card.classList.remove(
                    'em-card-configuration__pricing-card--error',
                );
            });

        $panel
            .querySelectorAll('[data-tariff] input[type="radio"]')
            .forEach(($input) => {
                $input.removeAttribute('aria-invalid');
            });
    }

    renderErrors($panel, errors) {
        Object.entries(errors).forEach(
            ([fieldName, messages]) => {
                const normalizedMessages = Array.isArray(messages)
                    ? messages
                    : [messages];
                const translatedMessages = normalizedMessages.map(
                    (message) => this.translateValidationMessage(message),
                );

                if (fieldName === '_form') {
                    this.renderPanelErrors(
                        $panel,
                        translatedMessages,
                    );
                    return;
                }

                this.renderFieldErrors(
                    $panel,
                    fieldName,
                    translatedMessages,
                );
            },
        );
    }

    translateValidationMessage(message) {
        const normalizedMessage = String(message || '');

        return this.validationMessages[normalizedMessage] || normalizedMessage;
    }

    renderPanelErrors($panel, messages) {
        const $panelError = this.getPanelErrorContainer($panel);

        if (!$panelError) {
            return;
        }

        messages.forEach((message) => {
            const $error = document.createElement('p');

            $error.className = 'form__error';
            $error.dataset.jsError = '';
            $error.textContent = message;

            $panelError.appendChild($error);
        });
    }

    renderFieldErrors($panel, fieldName, messages) {
        if (!this.shouldRenderFieldError($panel, fieldName, messages)) {
            return;
        }

        if (this.hasExistingRenderedError($panel, fieldName)) {
            return;
        }

        this.revealFieldForError($panel, fieldName);

        const errorFieldName = this.normalizeErrorFieldName(fieldName);
        const $groupErrorContainer = this.getGroupErrorContainer(
            $panel,
            errorFieldName,
        );

        if ($groupErrorContainer) {
            this.markGroupedFieldErrorState($panel, errorFieldName);
            this.appendErrors($groupErrorContainer, messages, 'span');
            return;
        }

        const $input = this.findPanelField($panel, fieldName);

        if (!$input) {
            this.renderPanelErrors($panel, messages);
            return;
        }

        const $fieldWrapper = $input.closest('.form__field');
        const $formElement = $input.closest('.form__element');
        const $tariffCard = $input.closest('[data-tariff]');
        const $featureCard = $input.closest(
            '[data-feature-wrapper]',
        );
        const $selectionCard = $input.closest(
            '.em-card-configuration__selection-card',
        );

        $fieldWrapper?.classList.add('form__field--error');

        $tariffCard?.classList.add(
            'em-card-configuration__pricing-card--error',
        );

        $featureCard?.classList.add(
            'em-card-configuration__feature-card--error',
        );

        $selectionCard?.classList.add(
            'em-card-configuration__selection-card--error',
        );

        const $errorContainer =
            $formElement ||
            $tariffCard ||
            $featureCard ||
            $selectionCard ||
            $input.parentElement;

        this.appendErrors($errorContainer, messages, 'span');
    }

    hasExistingRenderedError($panel, fieldName) {
        const errorFieldName = this.normalizeErrorFieldName(fieldName);
        const $input = this.findPanelField($panel, fieldName);
        const fieldId = $input?.id || '';
        const fieldErrorIdPrefix = fieldId ? `${fieldId}_error` : '';
        const hasServerRenderedFieldError = Boolean(
            fieldId &&
                Array.from(document.querySelectorAll('[id]')).some(
                    ($node) =>
                        $node.id === fieldErrorIdPrefix ||
                        $node.id.startsWith(`${fieldErrorIdPrefix}_`),
                ),
        );
        const $candidateContainers = [
            $input?.closest('.form__field'),
            $input?.closest('.form__element'),
            $input?.closest('[data-tariff]'),
            $input?.closest('[data-feature-wrapper]'),
            $input?.closest('.em-card-configuration__selection-card'),
            $panel.querySelector('[data-panel-error]'),
            $panel.querySelector('[data-tariff-errors]'),
            $panel.querySelector('[data-feature-errors]'),
        ].filter(Boolean);

        if (
            errorFieldName === 'cardTariff' ||
            errorFieldName.startsWith('extra')
        ) {
            return (
                hasServerRenderedFieldError ||
                $candidateContainers.some(($container) =>
                    Boolean($container.querySelector('.form__error')),
                )
            );
        }

        return (
            hasServerRenderedFieldError ||
            $candidateContainers.some(($container) =>
                Boolean($container.querySelector('.form__error')),
            ) ||
            $input?.closest('.form__field')?.classList.contains(
                'form__field--error',
            )
        );
    }

    shouldRenderFieldError($panel, fieldName, messages) {
        const errorFieldName = this.normalizeErrorFieldName(fieldName);

        if (errorFieldName === 'cardTariff') {
            return false;
        }

        return true;
    }

    appendErrors($container, messages, tagName) {
        if (!$container) {
            return;
        }

        messages.forEach((message) => {
            const $error = document.createElement(tagName);

            $error.className = 'form__error';
            $error.dataset.jsError = '';
            $error.textContent = message;

            $container.appendChild($error);
        });
    }

    getErrorContainers($panel) {
        return Array.from(
            $panel.querySelectorAll(
                '[data-panel-error], [data-tariff-errors], [data-feature-errors]',
            ),
        );
    }

    getPanelErrorContainer($panel) {
        return $panel.querySelector('[data-panel-error]');
    }

    getGroupErrorContainer($panel, fieldName) {
        if (fieldName === 'cardTariff') {
            return $panel.querySelector('[data-tariff-errors]');
        }

        if (fieldName.startsWith('extra')) {
            return $panel.querySelector('[data-feature-errors]');
        }

        return null;
    }

    markGroupedFieldErrorState($panel, fieldName) {
        if (fieldName === 'cardTariff') {
            $panel
                .querySelectorAll('[data-tariff]')
                .forEach(($card) => {
                    $card.classList.add(
                        'em-card-configuration__pricing-card--error',
                    );
                });

            return;
        }

        if (!fieldName.startsWith('extra')) {
            return;
        }

        const $input = this.findPanelField($panel, fieldName);
        const $featureCard = $input?.closest('[data-feature-wrapper]');

        $featureCard?.classList.add(
            'em-card-configuration__feature-card--error',
        );
    }

    findPanelField($panel, fieldName) {
        const normalizedFieldName = this.normalizeErrorFieldName(fieldName);
        const candidateNames = [
            String(fieldName || ''),
            normalizedFieldName,
        ].filter(Boolean);

        return Array.from(
            $panel.querySelectorAll(
                'input, select, textarea',
            ),
        ).find(($field) =>
            candidateNames.some((candidate) =>
                $field.name.endsWith(`[${candidate}]`),
            ),
        );
    }

    normalizeErrorFieldName(fieldName) {
        const normalizedFieldName = String(fieldName || '');
        const separatorTokens = normalizedFieldName.split(/[.[\]]+/).filter(Boolean);

        if (!separatorTokens.length) {
            return normalizedFieldName;
        }

        return separatorTokens[separatorTokens.length - 1];
    }

    revealFieldForError($panel, fieldName) {
        const $input = this.findPanelField($panel, fieldName);
        const $dependencyPanel = $input?.closest('[data-vin-panel]');

        if (!$dependencyPanel) {
            return;
        }

        $dependencyPanel.hidden = false;
        $dependencyPanel.setAttribute('aria-hidden', 'false');

        $dependencyPanel
            .querySelectorAll('input, select, textarea')
            .forEach(($field) => {
                $field.removeAttribute('tabindex');
            });
    }

    getInheritedFeatureNames() {
        return Array.from(
            new Set(
                Array.from(
                    this.$root.querySelectorAll(
                        '[data-feature][data-inherited-across-drive-type="true"]',
                    ),
                )
                    .map(($input) =>
                        this.normalizeValue($input.dataset.feature || ''),
                    )
                    .filter(Boolean),
            ),
        );
    }

    focusFirstError($panel) {
        const $field =
            $panel.querySelector(
                '.form__field--error input, ' +
                    '.form__field--error select, ' +
                    '.form__field--error textarea',
            ) ||
            $panel.querySelector(
                '.em-card-configuration__pricing-card--error input, ' +
                    '.em-card-configuration__feature-card--error input, ' +
                    '.em-card-configuration__selection-card--error input',
            );

        const $scrollTarget =
            $field || $panel.querySelector('.form__error');

        $scrollTarget?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });

        $field?.focus();
    }

    focusPanelErrorsIfPresent($panel, $tab) {
        if (
            !$panel ||
            !$tab ||
            !$tab.classList.contains('em-card-configuration__tab--error')
        ) {
            return;
        }

        this.focusFirstError($panel);
    }

    async handleNextClick(event) {
        event.preventDefault();

        if (this.isValidating) {
            return;
        }

        const $activeTab = this.getActiveTab();
        const $activePanel = $activeTab
            ? this.getPanel($activeTab.dataset.cardTab)
            : null;

        if ($activePanel && $activeTab) {
            const isValid = await this.validatePanel(
                $activePanel,
                $activeTab,
            );

            if (!isValid) {
                return;
            }
        }

        if (typeof this.$form.requestSubmit === 'function') {
            this.$form.requestSubmit();
            return;
        }

        this.$form.submit();
    }
}

export {CardConfiguration};

window.addEventListener('content.loaded', (event) => {
    const $context = event.detail?.$context || document;

    $context
        .querySelectorAll(
            '[data-card-configuration="root"]',
        )
        .forEach(($element) => {
            if ($element.API) {
                return;
            }

            const cardConfiguration =
                new CardConfiguration($element);

            cardConfiguration.initialize();
            $element.API = cardConfiguration;
        });
});
